import { useMemo } from "react";
import Map from "./Map";
import MapLegend from "./MapLegend";
import { decodePolyline } from "../utils/polyline";
import {
  buildPassengerBoardingMarkers,
  extractTripPassengerPoints,
  getPlannedRouteEncoded,
  isCompletedTripStatus,
} from "../utils/tripTracking";

const TripRoute = ({
  startPoint,
  endPoint,
  tripData = null,
  path,
  driverTrail = [],
  driverLocation = null,
  isLiveTrip = false,
  tripStatus = "",
  isSignalRConnected = false,
}) => {
  const plannedPathEncoded = path || getPlannedRouteEncoded(tripData);
  const decodedPath = useMemo(
    () => decodePolyline(plannedPathEncoded),
    [plannedPathEncoded],
  );
  const isCompletedTrip = isCompletedTripStatus(tripStatus);

  const passengerMarkers = useMemo(() => {
    const tripPoints = extractTripPassengerPoints(tripData);
    return buildPassengerBoardingMarkers(
      tripPoints,
      startPoint,
      endPoint,
      tripData?.requests ?? tripData?.trip_requests,
    );
  }, [tripData, startPoint, endPoint]);

  const passengerCount = useMemo(() => {
    const ids = new Set(
      extractTripPassengerPoints(tripData)
        .map((p) => p.addedBy)
        .filter((id) => id != null),
    );
    return ids.size;
  }, [tripData]);

  const showLiveBanner =
    isLiveTrip && (driverTrail.length > 0 || isSignalRConnected);

  return (
    <div className="bg-third rounded-3xl p-6 font-semibold">
      <div className="flex flex-col gap-6">
        <div className="flex md:flex-row flex-col gap-5 justify-between">
          <div className="flex flex-col gap-4">
            <p className="flex items-center gap-2 text-black">
              <span className="text-gray-400">Start Point:</span>
              {startPoint?.name_en || startPoint?.name || "N/A"}
            </p>

            <p className="flex items-center gap-2 text-black">
              <span className="text-gray-400">Destination:</span>
              {endPoint?.name_en || endPoint?.name || "N/A"}
            </p>

            <p className="flex items-center gap-2 text-black">
              <span className="text-gray-400">Planned route:</span>
              {decodedPath.length > 0
                ? `${decodedPath.length} points`
                : "No path data"}
            </p>

            {(isLiveTrip || isCompletedTrip) && (
              <p className="flex items-center gap-2 text-black">
                <span className="text-gray-400">Driver trail:</span>
                <span className="font-bold text-primary">
                  {driverTrail.length} GPS points
                </span>
                {isLiveTrip && isSignalRConnected && (
                  <span className="text-xs text-green-600 font-normal">
                    · Live
                  </span>
                )}
              </p>
            )}

            {passengerMarkers.length > 0 && (
              <p className="flex items-center gap-2 text-black">
                <span className="text-gray-400">Passenger stops:</span>
                <span>
                  {passengerCount} passenger(s) · {passengerMarkers.length}{" "}
                  markers
                </span>
              </p>
            )}

            {showLiveBanner && driverLocation && (
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-sm font-medium text-green-800 mb-1">
                  Live driver tracking
                </p>
                <p className="text-xs text-green-700">
                  {driverLocation.lat?.toFixed(6)},{" "}
                  {driverLocation.lng?.toFixed(6)}
                </p>
                {driverLocation.datetime && (
                  <p className="text-xs text-green-600">
                    {new Date(driverLocation.datetime).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            <p className="flex items-center gap-2 text-black">
              <span className="text-gray-400">Trip status:</span>
              {isCompletedTrip ? (
                <span className="font-bold text-green-600">
                  Completed — showing planned + actual route
                </span>
              ) : isLiveTrip ? (
                <span className="font-bold text-blue-600">
                  Ongoing — live tracking
                </span>
              ) : (
                <span className="font-bold capitalize">
                  {tripStatus?.replace(/_/g, " ") || "—"}
                </span>
              )}
            </p>

            <MapLegend
              isLiveTrip={isLiveTrip}
              isCompletedTrip={isCompletedTrip}
            />
          </div>

          <div className="md:w-1/2 w-full" style={{ minHeight: "400px" }}>
            <Map
              path={decodedPath}
              driverTrail={driverTrail}
              driverLocation={driverLocation}
              isLiveTrip={isLiveTrip}
              isCompletedTrip={isCompletedTrip}
              startPoint={startPoint}
              endPoint={endPoint}
              passengerMarkers={passengerMarkers}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripRoute;
